<template>
  <v-container>
    <v-dialog
      :value="value"
      @input="$emit('input', $event.target.value)"
      fullscreen
      transition="dialog-bottom-transition"
    >
      <v-card color="primary">
        <v-toolbar color="primary">
          <v-toolbar-title>
            <h3>{{ isEdit ? "Editing Cleaning Audit" : "New Cleaning Audit" }}</h3>
          </v-toolbar-title>
          <v-spacer />
          <v-btn icon @click="reset();">
            <v-icon>close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-stepper v-model="currentStep" vertical class="elevation-0">
          <v-stepper-step :complete="currentStep > 1" step="1" editable>Employee Name</v-stepper-step>
          <v-stepper-items>
            <v-stepper-content step="1">
              <v-form v-if="value" ref="employeeForm" @submit.prevent="currentStep++">
                <v-card-text>
                  <UserSearch
                    @employee-selected="employeeSelected"
                    :user="
                      isEdit
                        ? {
                            displayName: body.employee.name,
                            employeeID: body.employee.badgeID
                          }
                        : null
                    "
                  />
                </v-card-text>
                <v-card-actions>
                  <v-spacer />
                  <v-btn @click="reset();" flat>Cancel</v-btn>
                  <v-btn type="submit" flat color="secondary lighten-2">Continue</v-btn>
                </v-card-actions>
              </v-form>
            </v-stepper-content>

            <v-stepper-step :complete="currentStep > 2" step="2" editable>Audit Date</v-stepper-step>
            <v-stepper-content step="2">
              <v-form ref="dateForm" @submit.prevent="currentStep++">
                <v-card-text>
                  <v-menu
                    v-model="datePicker"
                    :close-on-content-click="true"
                    offset-y
                    min-width="290px"
                  >
                    <template v-slot:activator="{ on }">
                      <v-text-field
                        class="mx-2"
                        box
                        color="secondary"
                        v-model="body.auditDate"
                        readonly
                        v-on="on"
                      >
                        <template v-slot:label>
                          Audit Date
                          <small>(optional)</small>
                        </template>
                      </v-text-field>
                    </template>
                    <v-date-picker v-model="body.auditDate" color="secondary" no-title />
                  </v-menu>
                </v-card-text>

                <v-card-actions>
                  <v-spacer />
                  <v-btn type="submit" flat color="secondary lighten-2">Continue</v-btn>
                </v-card-actions>
              </v-form>
            </v-stepper-content>
            <v-stepper-step :complete="currentStep > 3" step="3" editable>Product Category</v-stepper-step>
            <v-stepper-content step="3">
              <v-form ref="categoryForm" @submit.prevent="currentStep++">
                <v-card-text>
                  <v-select
                    label="Select A Category"
                    box
                    color="secondary"
                    :items="categories"
                    v-model="body.productCategory"
                  ></v-select>
                  <v-text-field
                    box
                    color="secondary"
                    v-model="otherCategory"
                    label="Other category"
                    v-if="body.productCategory.toLowerCase() === 'other'"
                  />
                </v-card-text>

                <v-card-actions>
                  <v-spacer />
                  <v-btn type="submit" flat color="secondary lighten-2">Continue</v-btn>
                </v-card-actions>
              </v-form>
            </v-stepper-content>

            <v-stepper-step :complete="currentStep > 4" step="4" editable>Product Quantities</v-stepper-step>
            <v-stepper-content step="4">
              <v-form ref="quantityForm" @submit.prevent="createOrEdit">
                <v-card-text>
                  <v-layout
                    :row="
                      $vuetify.breakpoint.md ||
                        $vuetify.breakpoint.lg ||
                        $vuetify.breakpoint.xl
                    "
                    :column="$vuetify.breakpoint.xs || $vuetify.breakpoint.sm"
                  >
                    <v-flex xs12 class="pa-1">
                      <v-text-field
                        box
                        color="secondary"
                        v-model="body.quantityGiven"
                        type="number"
                        label="Amount Given"
                      />
                      <v-text-field
                        box
                        color="secondary"
                        v-model="body.quantityReceived"
                        type="number"
                        label="Amount Received"
                      />
                      <v-text-field
                        box
                        color="secondary"
                        v-model="body.quantityLeft"
                        type="number"
                        label="Amount Unfinished"
                      />
                      <v-text-field
                        box
                        color="secondary"
                        v-model="body.quantityNew"
                        type="number"
                        label="Amount New"
                      />
                      <v-text-field
                        box
                        color="secondary"
                        v-model="body.quantityCertified"
                        type="number"
                        label="Amount Certified"
                      />
                    </v-flex>
                    <v-flex xs12 class="pa-1">
                      <v-text-field
                        box
                        color="secondary"
                        v-model="body.quantityRefurbished"
                        type="number"
                        label="Amount Refurbished"
                      />
                      <v-text-field
                        box
                        color="secondary"
                        v-model="body.quantityPreowned"
                        type="number"
                        label="Amount Pre-Owned"
                      />
                      <v-text-field
                        box
                        color="secondary"
                        v-model="body.quantityBroken"
                        type="number"
                        label="Amount Broken"
                      />
                      <v-text-field
                        box
                        color="secondary"
                        v-model="body.quantityWrong"
                        type="number"
                        label="Amount Wrong Model"
                      />
                    </v-flex>
                  </v-layout>
                  <v-text-field
                    v-model="body.remarks"
                    label="Use this field to add any additional comments to this audit"
                    color="secondary"
                    box
                    class="mt-5"
                  />
                </v-card-text>
                <v-card-actions>
                  <v-layout
                    :row="
                      $vuetify.breakpoint.md ||
                        $vuetify.breakpoint.lg ||
                        $vuetify.breakpoint.xl
                    "
                    :column="$vuetify.breakpoint.xs || $vuetify.breakpoint.sm"
                  >
                    <v-btn flat @click="$refs.quantityForm.reset()">Reset Quantities</v-btn>
                    <v-spacer />
                    <v-btn type="submit" flat color="secondary lighten-2">Submit</v-btn>
                  </v-layout>
                </v-card-actions>
              </v-form>
            </v-stepper-content>
          </v-stepper-items>
        </v-stepper>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapGetters } from "vuex";
import UserSearch from "@/components/app/UserSearch";
import TableToolbar from "@/components/app/TableToolbar";
import LoginCheck from "@/mixins/LoginCheck";

export default {
  name: "Cleaning",
  components: {
    UserSearch,
    TableToolbar
  },

  props: ["value"],

  data() {
    return {
      dialogTitle: null,
      currentStep: 1,
      otherCategory: null,
      body: {
        auditor: {
          name: null,
          badgeID: null
        },
        employee: {
          name: null,
          badgeID: null
        },
        productCategory: "",
        quantityGiven: 0,
        quantityReceived: 0,
        quantityLeft: 0,
        quantityNew: 0,
        quantityCertified: 0,
        quantityRefurbished: 0,
        quantityPreowned: 0,
        quantityBroken: 0,
        quantityWrong: 0,
        auditDate: null,
        remarks: ""
      },
      isEdit: false,
      datePicker: false
    };
  },

  mixins: [LoginCheck],

  computed: {
    ...mapGetters({
      allAudits: "audits/getAllAudits",
      categories: "audits/getCategories",
      user: "app/getUser",
      edit: "audits/getEditAudit"
    })
  },

  watch: {
    edit(val) {
      if (val._id) {
        this.isEdit = true;
        val.auditDate = new Date(val.auditDate).toLocaleDateString();
        this.body = {
          auditor: {
            name: val.auditor.name,
            badgeID: val.auditor.badgeID
          },
          employee: {
            name: val.employee.name,
            badgeID: val.employee.badgeID
          },
          productCategory: this.categories.includes(val.productCategory)
            ? val.productCategory
            : "Other",
          quantityGiven: val.quantityGiven,
          quantityReceived: val.quantityReceived,
          quantityCertified: val.quantityCertified,
          quantityRefurbished: val.quantityRefurbished,
          quantityPreowned: val.quantityPreowned,
          quantityBroken: val.quantityBroken,
          quantityWrong: val.quantityWrong,
          quantityLeft: val.quantityLeft,
          quantityNew: val.quantityNew,
          remarks: val.remarks,
          auditDate: val.auditDate
            ? new Date(val.auditDate).toISOString().substr(0, 10)
            : ""
        };
      }
    }
  },

  mounted() {
    this.$store.dispatch("audits/getCategories", { module: "cleaning" });
  },

  methods: {
    employeeSelected(e) {
      let name = null;
      let badgeID = null;
      try {
        name = e.split("(")[0].trim();
        badgeID = e.match(/\d+/)[0];
      } catch {
        badgeID = "";
      }
      this.body.employee = {
        name,
        badgeID
      };
    },
    async createOrEdit() {
      if (this.body.productCategory.toLowerCase() === "other") {
        this.body.productCategory = this.otherCategory;
      }
      let total =
        parseInt(this.body.quantityNew) +
        parseInt(this.body.quantityLeft) +
        parseInt(this.body.quantityCertified) +
        parseInt(this.body.quantityRefurbished) +
        parseInt(this.body.quantityPreowned) +
        parseInt(this.body.quantityBroken) +
        parseInt(this.body.quantityWrong);

      if (parseInt(total) !== parseInt(this.body.quantityReceived)) {
        this.$store.commit("app/setSnackbar", {
          color: "red",
          text: "Check that your quantities add up to the amount received."
        });
      } else if (!this.body.employee.name) {
        this.$store.commit("app/setSnackbar", {
          color: "red",
          text: "You did not enter an employee!"
        });
      } else if (
        !this.body.productCategory ||
        this.body.productCategory == ""
      ) {
        this.$store.commit("app/setSnackbar", {
          color: "red",
          text: "You did not enter a category!"
        });
      } else {
        if (!this.isEdit) {
          this.body.auditor = {
            name: this.user.displayName,
            badgeID: this.user.employeeID
          };
          this.body.lastUpdate = new Date();
          await this.$store.dispatch("audits/create", {
            module: "cleaning",
            body: this.body
          });
        } else {
          this.body.lastUpdate = new Date();
          await this.$store.dispatch("audits/update", {
            module: "cleaning",
            id: this.edit._id,
            body: this.body
          });
        }
        this.reset();
      }
    },
    reset() {
      this.body = {
        auditor: {
          name: null,
          badgeID: null
        },
        employee: {
          name: null,
          badgeID: null
        },
        productCategory: "",
        quantityGiven: null,
        quantityReceived: null,
        quantityCertified: null,
        quantityRefurbished: null,
        quantityPreowned: null,
        quantityBroken: null,
        quantityWrong: null,
        quantityLeft: null,
        quantityNew: null,
        auditDate: null,
        remarks: null
      };
      this.currentStep = 1;
      this.isEdit = false;
      this.$refs.employeeForm.reset();
      this.$emit("input", false);
      this.$store.commit("audits/setEditAudit", []);
      this.$store.dispatch("audits/getAllAudits", {
        rowsPerPage: 10,
        sortBy: "dateCreated",
        descending: true,
        page: 1,
        from: null,
        to: null,
        search: null,
        module: "cleaning"
      });
    }
  }
};
</script>

<style scoped>
>>> .theme--dark.v-picker__body {
  background: #333c42 !important;
}
>>> .v-table.theme--dark tr:hover {
  cursor: pointer;
}
</style>
